import CoRegistrantForm from '@/components/deal/co-registrant/co-registrant-form';
import CreateDeliveryAddressButton from '@/components/deal/delivery-address/delivery-address-create-button';
import DeleteDeliveryAddressButton from '@/components/deal/delivery-address/delivery-address-delete-button';
import PrimaryCustomerEmailEditor from '@/components/deal/primary-customer/primary-customer-email-editor';
import PrimaryCustomerNameEditor from '@/components/deal/primary-customer/primary-customer-name-editor';
import PrimaryCustomerPhoneEditor from '@/components/deal/primary-customer/primary-customer-phone-editor';
import AddressForm from '@/components/ui/address-form/address-form';
import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import HoverClickStay from '@/components/ux/hover-click-stay';
import { formatAddress } from '@/lib/utils';
import { Deal } from '@/models/registry';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Column, Row } from './non-mobile-main';

export const NonMobileCustomerRow = observer(
  ({ deal, showCoregistrant }: { deal: Deal; showCoregistrant: boolean }) => {
    const customerRegistrationAddress = deal.primary_customer?.addresses.find(
      (address) => address.address_type === 'registration',
    );
    const customerDeliveryAddress = deal.primary_customer?.addresses.find(
      (address) => address.address_type === 'delivery',
    );
    return (
      <Row>
        <Column align="start">
          <div className="flex flex-col gap-1">
            <PrimaryCustomerNameEditor />
            <PrimaryCustomerEmailEditor />
            <PrimaryCustomerPhoneEditor />
          </div>

          <div className="flex flex-col gap-0">
            <div className="flex flex-row gap-2 items-center">
              <HoverClickStay
                key="registration-hover"
                className="gap-0"
                tether={
                  deal.primary_customer &&
                  !customerDeliveryAddress && (
                    <CreateDeliveryAddressButton
                      dealId={deal.id}
                      customerId={deal.primary_customer.id}
                    />
                  )
                }
              >
                <h5 className="scroll-m-20 text-lg  tracking-tight w-full">
                  Registration Address
                </h5>
              </HoverClickStay>
              <ConfirmationDot
                targetId={deal.id}
                targetType={ConfirmationTargetType.Deal}
                field={ConfirmationField.CustomerRegistrationAddress}
                value={observable.box(
                  formatAddress(customerRegistrationAddress),
                )}
              />
            </div>
            {deal.primary_customer && customerRegistrationAddress && (
              <AddressForm
                address={customerRegistrationAddress}
                focusOnMount={false}
                dealId={deal.id}
                customerIdOrCoregistrantId={deal.primary_customer.id}
                customerType={deal.primary_customer.customer_type}
              />
            )}
          </div>

          {customerDeliveryAddress && deal.primary_customer && (
            <div className="flex flex-col gap-0">
              <div className="flex flex-row gap-2 items-center">
                <HoverClickStay
                  key="delivery-address-hover"
                  className="justify-between"
                  tether={
                    <DeleteDeliveryAddressButton
                      dealId={deal.id}
                      customerId={deal.primary_customer.id}
                      deliveryAddressId={customerDeliveryAddress.id}
                    />
                  }
                >
                  <h5 className="scroll-m-20 text-lg  tracking-tight w-full">
                    Delivery Address
                  </h5>
                </HoverClickStay>
                <ConfirmationDot
                  targetId={deal.id}
                  targetType={ConfirmationTargetType.Deal}
                  field={ConfirmationField.CustomerDeliveryAddress}
                  value={observable.box(formatAddress(customerDeliveryAddress))}
                />
              </div>
              <AddressForm
                address={customerDeliveryAddress}
                focusOnMount={false}
                dealId={deal.id}
                customerIdOrCoregistrantId={deal.primary_customer.id}
                customerType={deal.primary_customer.customer_type}
              />
            </div>
          )}
        </Column>

        <Column align="end">
          {deal.co_registrant && (
            <div className="flex flex-col gap-0">
              <CoRegistrantForm
                focusOnMount={showCoregistrant}
                coRegistrant={
                  deal.co_registrant || {
                    id: '',
                    customer_type: 'co_registrant',
                    full_name: '',
                    email: '',
                    mobile_number: '',
                  }
                }
                address={
                  (deal.co_registrant &&
                    deal.co_registrant.addresses.find(
                      (address) => address.address_type === 'registration',
                    )) || {
                    id:
                      deal.co_registrant?.addresses.find(
                        (address) => address.address_type === 'registration',
                      )?.id || '',
                    customer_id: deal.co_registrant?.id || '',
                    address_type: 'registration',
                    address_line_1: '',
                    address_line_2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                  }
                }
                dealId={deal.id}
              />
            </div>
          )}
        </Column>
      </Row>
    );
  },
);
